import { PositionWithIdentity } from "../../utils/positionIdentity";
import {
  canBind,
  decideHide,
  HideVerdict,
  PositionFingerprint,
  toFingerprint,
} from "./positionMatch";

/**
 * Closes the trader has submitted but the server has not yet confirmed.
 *
 * ONE registry, shared by every consumer. There used to be two independent
 * tombstone maps with two independently-maintained 15s constants — one behind
 * the paginated `/positions` table, one behind the `/account`-derived map that
 * feeds the trade form, chart marks and the Positions page. The same screen
 * renders both, so they could disagree about whether a position exists.
 *
 * Nothing here expires by time. A confirmed close is NOT proof the position is
 * gone: the router turns a close into a reduce-only market order against the
 * CLOB, so a thin book can leave a remainder, and the gasless path never waits
 * for the transaction at all. So a hide is provisional — released by positive
 * evidence, and bounded by counted contrary observations rather than a clock.
 */

/** How many times the server may keep showing an unchanged row before we stop hiding it. */
export const MAX_UNCONFIRMED_SIGHTINGS = 6;

/**
 * How many CONSECUTIVE complete responses may omit a pending close before the
 * entry is retired.
 *
 * Absence is not proof the close was indexed — a wedged indexer looks the same
 * from here, which is why one absent response means nothing. This is a bounded
 * cleanup of entries that are no longer doing any work, so the registry cannot
 * grow for the life of the tab and a long-dead fingerprint cannot hide a new
 * position that happens to match it.
 */
export const MAX_ABSENCES_BEFORE_RETIRING = 3;

export type PendingCloseStatus = "pending" | "unresolved";

export interface PendingClose {
  id: string;
  account: string;
  fingerprint: PositionFingerprint;
  /** `receipt` waited for the chain; `broadcast` only saw a relayer accept it. */
  proof: "receipt" | "broadcast";
  submittedAt: number;
  /** Server responses that still showed an unchanged matching row. */
  sightings: number;
  /** Consecutive COMPLETE responses that did not contain this position at all. */
  absences: number;
  status: PendingCloseStatus;
}

interface RegistrySnapshot {
  version: number;
  byAccount: Readonly<Record<string, readonly PendingClose[]>>;
}

const listeners = new Set<() => void>();
let state: RegistrySnapshot = { version: 0, byAccount: {} };
let nextId = 0;
/**
 * How close together two identical observations must be to count as one.
 *
 * This is NOT an expiry — nothing here ages out by clock. It only separates
 * "several mounted consumers reporting the same server response" (milliseconds
 * apart) from "the server returned the same list again" (seconds apart), which
 * must still count as a fresh contradiction.
 */
export const OBSERVATION_COALESCE_MS = 400;

// Last response each account was observed with, and when.
const lastObserved = new Map<string, { signature: string; at: number }>();

const commit = (byAccount: Record<string, readonly PendingClose[]>) => {
  state = { version: state.version + 1, byAccount };
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One bad subscriber must not stop the others from being told.
    }
  });
};

const accountKey = (account: string) => account.toLowerCase();

/** Subscribe/getSnapshot pair for `useSyncExternalStore`. */
export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Identity-stable between commits, as `useSyncExternalStore` requires. */
export const getSnapshot = (): RegistrySnapshot => state;

export const getPendingCloses = (
  account: string | undefined
): readonly PendingClose[] =>
  (account && state.byAccount[accountKey(account)]) || [];

/** Two fingerprints describing the same position. */
const sameFingerprint = (a: PositionFingerprint, b: PositionFingerprint) =>
  a.positionKey === b.positionKey &&
  a.instrumentId === b.instrumentId &&
  a.instrumentName === b.instrumentName &&
  a.side === b.side &&
  a.marginType === b.marginType &&
  a.amount === b.amount &&
  a.avgEntryPrice === b.avgEntryPrice;

export const openPendingClose = (input: {
  account: string;
  position: PositionWithIdentity;
  proof: "receipt" | "broadcast";
  now?: number;
}): string => {
  const key = accountKey(input.account);
  const fingerprint = toFingerprint(input.position);

  // ONE entry per closed position, however many consumers report it.
  //
  // `usePositionsWSS` is mounted at up to seven sites (three or four at once on
  // the perps page) and each subscribes to the trade bus, and the paginated
  // realtime hook registers as well — so a single close was creating four or
  // five identical entries. They hide and release together, so the visible
  // behaviour looked right, but every one of them consumed the shared
  // MAX_UNCONFIRMED_SIGHTINGS budget independently, which is half of why that
  // budget was being exhausted almost immediately (see `observeRows`).
  const existing = (state.byAccount[key] || []).find((entry) =>
    sameFingerprint(entry.fingerprint, fingerprint)
  );
  if (existing) return existing.id;

  nextId += 1;
  const id = `pending-close-${nextId}`;
  const entry: PendingClose = {
    id,
    account: key,
    fingerprint,
    proof: input.proof,
    submittedAt: input.now ?? Date.now(),
    sightings: 0,
    absences: 0,
    status: "pending",
  };
  commit({ ...state.byAccount, [key]: [...(state.byAccount[key] || []), entry] });
  return id;
};

export const releasePendingClose = (id: string): void => {
  const byAccount: Record<string, readonly PendingClose[]> = {};
  let found = false;
  Object.entries(state.byAccount).forEach(([key, entries]) => {
    const next = entries.filter((entry) => {
      if (entry.id === id) {
        found = true;
        return false;
      }
      return true;
    });
    if (next.length) byAccount[key] = next;
  });
  if (found) commit(byAccount);
};

/**
 * Drop everything for an account. Called when the connected account changes: a
 * previous account's unconfirmed close says nothing about this one's data, and
 * resurrecting a stale claim later is worse than silence.
 */
export const clearAccount = (account: string | undefined): void => {
  if (!account) return;
  const key = accountKey(account);
  if (!state.byAccount[key]) return;
  const byAccount = { ...state.byAccount };
  delete byAccount[key];
  lastObserved.delete(key);
  commit(byAccount);
};

/**
 * Release every pending close that could refer to this position.
 *
 * Called with a position the trader just confirmed OPEN. That is direct
 * evidence the market and side are live again, so any close still pending for
 * it is superseded — this is what stops a re-open at an identical size and
 * price from being mistaken for the stale closed row.
 */
export const releasePendingClosesFor = (
  account: string | undefined,
  position: PositionWithIdentity
): void => {
  getPendingCloses(account).forEach((entry) => {
    if (canBind(entry.fingerprint, position)) releasePendingClose(entry.id);
  });
};

/** Test seam. */
export const resetPendingCloses = (): void => {
  nextId = 0;
  lastObserved.clear();
  commit({});
};

/**
 * Flag set on a row we cannot confidently hide.
 *
 * Present when a close is pending but more than one row could be the one that
 * was closed — so hiding either would remove a position the trader still holds.
 * The row stays visible and carries this marker, letting the UI say "closing"
 * instead of presenting it as an ordinary live position.
 */
export const SETTLING_FLAG = "__tradeSyncSettling" as const;

export interface ClassifiedRows {
  /** Indices the UI should hide outright. */
  hidden: number[];
  /** Indices to render as settling: we cannot prove which row is closing. */
  settling: number[];
}

/**
 * Decide, for one list of server rows, what a pending close may hide.
 *
 * Pure with respect to the registry EXCEPT for sighting bookkeeping, which is
 * why callers must invoke `observeRows` from an effect rather than during
 * render. `classifyRows` itself never mutates.
 */
export const classifyRows = (
  account: string | undefined,
  rows: readonly PositionWithIdentity[],
  listComplete: boolean
): ClassifiedRows => {
  const hidden = new Set<number>();
  const settling = new Set<number>();

  getPendingCloses(account).forEach((entry) => {
    const verdict: HideVerdict = decideHide(
      entry.fingerprint,
      rows,
      listComplete
    );
    // An entry we have stopped trusting is shown, not hidden — but it is
    // flagged so the UI never presents it as an ordinary live position.
    if (entry.status === "unresolved") {
      if (verdict.kind === "hide") settling.add(verdict.index);
      if (verdict.kind === "ambiguous") verdict.indices.forEach((i) => settling.add(i));
      return;
    }
    if (verdict.kind === "hide") hidden.add(verdict.index);
    if (verdict.kind === "ambiguous") {
      verdict.indices.forEach((i) => settling.add(i));
    }
  });

  return {
    hidden: [...hidden].filter((i) => !settling.has(i)),
    settling: [...settling],
  };
};

/**
 * Record what a server response showed, releasing or ageing entries.
 *
 * Release conditions, all positive:
 *  - a bindable row is provably a different position (a re-open), or
 *  - the caller reports independent confirmation the close was indexed.
 *
 * ABSENCE of a matching row is deliberately NOT a release condition — it is a
 * no-op. That is what removes the wedged-indexer problem: a close whose row
 * never comes back simply keeps a harmless entry until the account changes.
 */
export const observeRows = (
  account: string | undefined,
  rows: readonly PositionWithIdentity[],
  listComplete: boolean,
  /** Injected by tests so a sequence of responses does not need real time. */
  now?: number
): void => {
  const entries = getPendingCloses(account);
  if (!entries.length) return;

  const key = accountKey(account as string);

  // Count RESPONSES, not callers.
  //
  // `MAX_UNCONFIRMED_SIGHTINGS` is meant to read as "the server contradicted us
  // six times". It was counting invocations: this is called once per `/account`
  // change and once per websocket snapshot in EVERY mounted `usePositionsWSS`
  // (three or four at once on the perps page), plus once from the paginated
  // hook — so ONE server response advanced each entry by four to six, and the
  // whole budget was spent about a second after the close. The row then came
  // back marked "Close pending" almost immediately, which is the intermittent
  // behaviour the browser specs kept catching.
  //
  // Consumers reacting to the same response all fire within a few milliseconds
  // of each other, whereas a genuine repeat poll is seconds later. So identical
  // rows are treated as ONE observation only inside a short window — content
  // alone is not enough, because a server that keeps returning the same list IS
  // contradicting us again each time it does, and those must still count.
  const signature = `${listComplete ? "C" : "P"}:${rows
    .map((row) =>
      [
        row.position_key || row.positionKey || "",
        // Both, not one-or-the-other: two rows can share an instrument_id and
        // differ only by name.
        row.instrument_id || "",
        row.instrument_name || "",
        row.side || "",
        row.margin_type || "",
        row.amount ?? "",
        row.avg_entry_price ?? "",
      ].join(":")
    )
    .join("|")}`;
  const at = now ?? Date.now();
  const previous = lastObserved.get(key);
  if (
    previous &&
    previous.signature === signature &&
    at - previous.at < OBSERVATION_COALESCE_MS
  ) {
    return;
  }
  lastObserved.set(key, { signature, at });

  let changed = false;

  const next = entries.reduce<PendingClose[]>((acc, entry) => {
    const verdict = decideHide(entry.fingerprint, rows, listComplete);

    if (verdict.kind === "diverged") {
      changed = true;
      return acc; // released: this row is provably not the one we closed
    }

    if (verdict.kind === "hide" || verdict.kind === "ambiguous") {
      // Still contradicted: the row we closed is still being returned.
      const sightings = entry.sightings + 1;
      const status: PendingCloseStatus =
        sightings >= MAX_UNCONFIRMED_SIGHTINGS ? "unresolved" : entry.status;
      if (
        sightings !== entry.sightings ||
        status !== entry.status ||
        entry.absences !== 0
      ) {
        changed = true;
        acc.push({ ...entry, sightings, status, absences: 0 });
        return acc;
      }
      acc.push(entry);
      return acc;
    }

    // `no-match`: the fingerprint is not in this response at all.
    //
    // Absence still is NOT proof the close was indexed — that is the wedged-
    // indexer trap this module was built to avoid, and one absent response means
    // nothing. But an entry that survives to here is doing no work: it hides
    // nothing, and it is retained only in case the row comes back. Kept forever,
    // it accumulates for the life of the tab (`classifyRows` is O(entries × rows)
    // on several surfaces) and can eventually hide a genuinely NEW position whose
    // size and entry price happen to match the old fingerprint.
    //
    // So absence is counted, not trusted: after several CONSECUTIVE complete
    // responses without it, the entry is retired. Deliberately separate from the
    // contrary-sighting counter, and only on complete lists — a partial page
    // proves nothing about a row that might be on another page.
    if (listComplete) {
      const absences = entry.absences + 1;
      if (absences >= MAX_ABSENCES_BEFORE_RETIRING) {
        changed = true;
        return acc; // retired
      }
      changed = true;
      acc.push({ ...entry, absences });
      return acc;
    }

    acc.push(entry);
    return acc;
  }, []);

  if (!changed) return;
  const byAccount = { ...state.byAccount };
  if (next.length) byAccount[key] = next;
  else delete byAccount[key];
  commit(byAccount);
};
